// Zodiac + age, derived from a birthday string (profile spec §4.2 / §2.1 / §2.2).
//
// These mirror `backend/app/modules/profile/service/zodiac.py` exactly and must
// stay in step with it. The server already sends `zodiac` and `age_turning`
// on every profile read, so the functions here exist for the two places the
// server cannot help: the onboarding picker, which has to label a day *before*
// the profile is saved, and the anniversary card's by-the-year table, which
// needs an age for years that have not been read from any profile.
//
// A stored sign could disagree with the birthday it came from, which is why
// this is a pure function on both sides rather than a column.
//
// **The table is verified, not recalled.** Profile spec §2.1 says
// "2/29 的星座按 2/19–3/20 歸水瓶座", which is wrong twice: 2/19–3/20 is 雙魚座 and
// 水瓶座 runs 1/20–2/18. Checking the full table against six sources turned
// up three more boundaries this file had wrong by a day — 巨蟹座 6/21,
// 天蠍座 10/23, 射手座 11/22 — each of which misfiles a real person for a day
// a year. Mirrors `backend/app/modules/profile/service/zodiac.py`; the two
// tables must stay identical, and `test_profile.py` on the backend pins the
// boundaries that this file cannot test on its own.
//
// Sources: farmersalmanac.com/birthday-astrology-february ·
// starryastrology.com/zodiac-signs-months-dates-and-order ·
// astrologybay.com/zodiac-date-ranges

/** `YYYY-MM-DD` → `{y, m, d}`, or null for anything unparseable. */
export function parseBirthday(value: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

type Entry = readonly [startMonth: number, startDay: number, sign: string];

/** Chronological; each entry owns `[start, next_start)`. */
const ZODIAC_TABLE: readonly Entry[] = [
  [1, 20, "水瓶座"],
  [2, 19, "雙魚座"],
  [3, 21, "白羊座"],
  [4, 20, "金牛座"],
  [5, 21, "雙子座"],
  [6, 21, "巨蟹座"],
  [7, 23, "獅子座"],
  [8, 23, "處女座"],
  [9, 23, "天秤座"],
  [10, 23, "天蠍座"],
  [11, 22, "射手座"],
  [12, 22, "摩羯座"],
];

/**
 * 2/29 exists only every fourth year and has no entry of its own in any
 * zodiac table. It falls inside the 雙魚座 range, which is also what
 * "跨界取後界" describes: the sign containing the days that follow, not the
 * ones before.
 */
const FEB_29_SIGN = "雙魚座";

export function zodiacSign(birthday: string | null | undefined): string | null {
  const parsed = parseBirthday(birthday);
  if (!parsed) return null;
  if (parsed.m === 2 && parsed.d === 29) return FEB_29_SIGN;
  // Latest boundary at or before the date, so the table is walked backwards.
  for (let i = ZODIAC_TABLE.length - 1; i >= 0; i -= 1) {
    const entry = ZODIAC_TABLE[i];
    if (!entry) continue; // unreachable; satisfies noUncheckedIndexedAccess
    const [sm, sd, sign] = entry;
    if (parsed.m > sm || (parsed.m === sm && parsed.d >= sd)) return sign;
  }
  // Jan 1–19 belongs to 摩羯座, which wraps across the new year.
  return "摩羯座";
}

/**
 * The age reached during `year` — the "今年 N 歲" number.
 *
 * Not "years elapsed so far": profile spec §2.1 lists both phrasings the card
 * may use ("今年將滿 N 歲" before the birthday, "今年 N 歲" on it) and they
 * carry the same N. The UI picks the wording; the number does not move
 * mid-year, so it must not be computed from a date comparison.
 */
export function ageReaching(birthday: string | null | undefined, year: number): number | null {
  const parsed = parseBirthday(birthday);
  if (!parsed) return null;
  return year - parsed.y;
}

/** Completed years as of `today`. Differs by one before the birthday. */
export function ageNow(
  birthday: string | null | undefined,
  today: Date = new Date(),
): number | null {
  const parsed = parseBirthday(birthday);
  if (!parsed) return null;
  const reached = ageReaching(birthday, today.getFullYear());
  if (reached === null) return null;
  const hadBirthday =
    today.getMonth() + 1 > parsed.m ||
    (today.getMonth() + 1 === parsed.m && today.getDate() >= parsed.d);
  return hadBirthday ? reached : reached - 1;
}

/** Days from `today` to the next occurrence of an MM-DD, this year or next. */
export function daysUntilBirthday(
  birthday: string | null | undefined,
  today: Date = new Date(),
): number | null {
  const parsed = parseBirthday(birthday);
  if (!parsed) return null;
  const year = today.getFullYear();
  const thisYear = new Date(year, parsed.m - 1, parsed.d);
  // `new Date(2027, 1, 29)` silently becomes Mar 1 in a common year, which is
  // the same invention ADR-0050 §5 refuses. Compare the parts instead.
  if (thisYear.getMonth() + 1 !== parsed.m || thisYear.getDate() !== parsed.d) {
    return daysBetween(today, new Date(year + 1, parsed.m - 1, parsed.d));
  }
  const diff = daysBetween(today, thisYear);
  return diff >= 0 ? diff : daysBetween(today, new Date(year + 1, parsed.m - 1, parsed.d));
}

function daysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b - a) / 86_400_000);
}

export interface YearRow {
  year: number;
  age: number;
  isCurrent: boolean;
}

/**
 * The by-the-year table on the birthday card (profile spec §3.5, P10).
 *
 * Starts at the year the member joined heshe, **not** their birth year, and
 * stops at the current year — the spec is explicit that it lists only years
 * that have happened, so a 30-year-old who joined in 2026 sees one row, not
 * thirty.
 */
export function birthdayYearTable(
  birthday: string | null | undefined,
  joinedAt: string | null | undefined,
  today: Date = new Date(),
): YearRow[] {
  const parsed = parseBirthday(birthday);
  if (!parsed) return [];
  const joined = parseYear(joinedAt) ?? today.getFullYear();
  const start = Math.max(joined, parsed.y);
  const end = today.getFullYear();
  const rows: YearRow[] = [];
  for (let year = start; year <= end; year += 1) {
    rows.push({ year, age: year - parsed.y, isCurrent: year === end });
  }
  return rows;
}

function parseYear(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{4})/.exec(value.trim());
  return match ? Number(match[1]) : null;
}
